<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-header d-flex justify-content-between">
            <div class="header-title">
              <h4 class="card-title">알리바바 API 사용량 모니터링</h4>
            </div>
          </div>
          <div class="card-body">
            <!-- 필터 섹션 -->
            <div class="row mb-4">
              <div class="col-md-3">
                <label for="startDate">시작 날짜</label>
                <input
                  type="date"
                  id="startDate"
                  v-model="filters.startDate"
                  class="form-control"
                  @change="loadLogs"
                >
              </div>
              <div class="col-md-3">
                <label for="endDate">종료 날짜</label>
                <input
                  type="date"
                  id="endDate"
                  v-model="filters.endDate"
                  class="form-control"
                  @change="loadLogs"
                >
              </div>
              <div class="col-md-3">
                <label for="emailFilter">이메일</label>
                <input
                  type="text"
                  id="emailFilter"
                  v-model="filters.email"
                  class="form-control"
                  placeholder="이메일 검색"
                  @input="debounceLoadLogs"
                >
              </div>
              <div class="col-md-3">
                <label for="featureFilter">기능</label>
                <select
                  id="featureFilter"
                  v-model="filters.feature"
                  class="form-control"
                  @change="loadLogs"
                >
                  <option value="">전체</option>
                  <option value="getImageSearch">이미지 검색</option>
                  <option value="getProductInfo">상품 정보</option>
                  <option value="translateText">번역</option>
                  <option value="removeImageBackground">배경 제거</option>
                </select>
              </div>
            </div>

            <!-- 통계 카드 -->
            <div class="row mb-4">
              <div class="col-md-4">
                <div class="card bg-primary text-white">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <div>
                        <h3 class="mb-0">{{ statistics.totalRequests }}</h3>
                        <p class="mb-0">총 요청 수</p>
                      </div>
                      <div class="align-self-center">
                        <i class="ri-bar-chart-line ri-2x"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card bg-success text-white">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <div>
                        <h3 class="mb-0">{{ statistics.uniqueUsers }}</h3>
                        <p class="mb-0">사용자 수</p>
                      </div>
                      <div class="align-self-center">
                        <i class="ri-user-line ri-2x"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="card bg-info text-white">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <div>
                        <h3 class="mb-0">{{ todayRequests }}</h3>
                        <p class="mb-0">오늘 요청 수</p>
                      </div>
                      <div class="align-self-center">
                        <i class="ri-calendar-line ri-2x"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 일별 사용량 차트 -->
            <div class="row mb-4" v-if="statistics.dailyStats && statistics.dailyStats.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <h5 class="card-title">일별 사용량 추이</h5>
                  </div>
                  <div class="card-body">
                    <ApexChart element="dailyChart" :chartOption="dailyChartOptions" />
                  </div>
                </div>
              </div>
            </div>

            <!-- 로그 테이블 -->
            <div class="card">
              <div class="card-header">
                <h5>상세 사용 내역</h5>
              </div>
              <div class="card-body">
                <CDataTable
                    storage-key="AdminApiMonitor"
                  :headers="tableHeaders"
                  :items="logs"
                  itemKey="_id"
                  :show-select="false"
                  height="600px"
                >
                  <template v-slot:feature="{ item }">
                    <span class="badge" :class="getFeatureBadgeClass(item.feature)">
                      {{ getFeatureName(item.feature) }}
                    </span>
                  </template>
                  <template v-slot:date="{ item }">
                    {{ formatDate(item.date) }}
                  </template>
                </CDataTable>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ApexChart from '../../../components/charts/ApexChart'
import CDataTable from '../../../components/common/CDataTable'

export default {
  name: 'AdminApiMonitor',
  components: {
    ApexChart,
    CDataTable
  },
  data() {
    return {
      logs: [],
      loading: false,
      filters: {
        startDate: this.getDateString(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)), // 7일 전
        endDate: this.getDateString(new Date()),
        email: '',
        feature: ''
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 50
      },
      statistics: {
        totalRequests: 0,
        uniqueUsers: 0,
        dailyStats: []
      },
      debounceTimer: null,
      tableHeaders: [
        { text: '날짜', value: 'date', isSlot: true },
        { text: '이메일', value: 'email' },
        { text: 'IP 주소', value: 'ip' },
        { text: '기능', value: 'feature', isSlot: true },
        { text: '요청 수', value: 'requestCount' }
      ]
    }
  },
  computed: {
    todayRequests() {
      const today = this.getDateString(new Date())
      const todayStat = this.statistics.dailyStats.find(stat => stat.date === today)
      return todayStat ? todayStat.requests : 0
    },
    dailyChartOptions() {
      if (!this.statistics.dailyStats || this.statistics.dailyStats.length === 0) {
        return {}
      }

      return {
        chart: {
          type: 'line',
          height: 350,
          toolbar: {
            show: false
          }
        },
        series: [{
          name: '일별 요청 수',
          data: this.statistics.dailyStats.map(stat => stat.requests)
        }],
        xaxis: {
          categories: this.statistics.dailyStats.map(stat => stat.date),
          title: {
            text: '날짜'
          }
        },
        yaxis: {
          title: {
            text: '요청 수'
          }
        },
        stroke: {
          curve: 'smooth',
          width: 2
        },
        colors: ['#4f46e5'],
        grid: {
          borderColor: '#e7e7e7',
          row: {
            colors: ['#f3f3f3', 'transparent'],
            opacity: 0.5
          }
        }
      }
    }
  },
  mounted() {
    this.loadLogs()
  },
  methods: {
    async loadLogs() {
      this.loading = true
      try {
        const response = await axios.post('/api/admin/alibaba-api-logs', {
          ...this.filters,
          page: this.pagination.currentPage,
          limit: this.pagination.limit
        })

        if (response.data.success) {
          this.logs = response.data.logs
          this.pagination = response.data.pagination
          this.statistics = response.data.statistics
        } else {
          this.$toast.error(response.data.message || '데이터 로드 실패')
        }
      } catch (error) {
        console.error('API 로그 조회 오류:', error)
        this.$toast.error('데이터 로드 중 오류가 발생했습니다.')
      } finally {
        this.loading = false
      }
    },
    debounceLoadLogs() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.pagination.currentPage = 1
        this.loadLogs()
      }, 500)
    },

    formatDate(date) {
      return new Date(date).toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    getDateString(date) {
      return date.toISOString().split('T')[0]
    },
    getFeatureName(feature) {
      const names = {
        'getImageSearch': '이미지 검색',
        'getProductInfo': '상품 정보',
        'translateText': '번역',
        'removeImageBackground': '배경 제거'
      }
      return names[feature] || feature
    },
    getFeatureBadgeClass(feature) {
      const classes = {
        'getImageSearch': 'badge-primary',
        'getProductInfo': 'badge-success',
        'translateText': 'badge-info',
        'removeImageBackground': 'badge-warning'
      }
      return classes[feature] || 'badge-secondary'
    }
  }
}
</script>

<style scoped>
.badge {
  font-size: 0.75em;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.table th {
  border-top: none;
  font-weight: 600;
}

.pagination-sm .page-link {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}
</style>
